import type { ReactNode } from "react";
import { CheckboxField } from "./CheckboxField";
import { CountField } from "./CountField";
import {
    CounterpartyField,
    type CounterpartyFieldMultiplePath,
    type CounterpartyFieldMultipleProps,
    type CounterpartyFieldPath,
    type CounterpartyFieldSingleProps,
} from "./CounterpartyField";
import {
    DateField,
    type DateFieldPath,
    type DateFieldProps,
} from "./DateField";
import {
    DateRangeField,
    type DateRangeFieldPath,
    type DateRangeFieldProps,
} from "./DateRangeField";
import { EmailsField } from "./EmailsField";
import { EntityField } from "./EntityField";
import {
    FinancialAccountField,
    type FinancialAccountFieldMultiplePath,
    type FinancialAccountFieldMultipleProps,
    type FinancialAccountFieldPath,
    type FinancialAccountFieldSingleProps,
} from "./FinancialAccountField";
import {
    LedgerAccountField,
    type LedgerAccountFieldPath,
    type LedgerAccountFieldProps,
} from "./LedgerAccountField";
import {
    MoneyField,
    type MoneyFieldPath,
    type MoneyFieldProps,
} from "./MoneyField";
import { PhoneField } from "./PhoneField";
import {
    RadioField,
    type RadioFieldPath,
    type RadioFieldProps,
} from "./RadioField";
import {
    SelectField,
    type SelectFieldPath,
    type SelectFieldProps,
} from "./SelectField";
import { SwitchField } from "./SwitchField";
import { TextAreaField } from "./TextAreaField";
import { TextField } from "./TextField";

/**
 * Kit fields bound to one form's `Values`. Text, TextArea, Count, Entity,
 * Emails, Phone, Checkbox and Switch props do not depend on the chosen path,
 * so they are instantiated with `Values`. Date, DateRange, Money, Select,
 * Radio and LedgerAccount props do (the `emptyValue` a path allows, the option values
 * it accepts), so they stay generic in
 * `Name` and infer it from `name`. FinancialAccount and Counterparty add a
 * second signature for `multiple`, whose paths hold id arrays.
 */
export interface FormikFields<Values> {
    Text: typeof TextField<Values>;
    TextArea: typeof TextAreaField<Values>;
    Count: typeof CountField<Values>;
    Entity: typeof EntityField<Values>;
    Emails: typeof EmailsField<Values>;
    Phone: typeof PhoneField<Values>;
    Checkbox: typeof CheckboxField<Values>;
    Switch: typeof SwitchField<Values>;
    Date: <Name extends DateFieldPath<Values>>(
        props: DateFieldProps<Values, Name>,
    ) => ReactNode;
    DateRange: <Name extends DateRangeFieldPath<Values>>(
        props: DateRangeFieldProps<Values, Name>,
    ) => ReactNode;
    Money: <Name extends MoneyFieldPath<Values>>(
        props: MoneyFieldProps<Values, Name>,
    ) => ReactNode;
    Select: <Name extends SelectFieldPath<Values>>(
        props: SelectFieldProps<Values, Name>,
    ) => ReactNode;
    Radio: <Name extends RadioFieldPath<Values>>(
        props: RadioFieldProps<Values, Name>,
    ) => ReactNode;
    LedgerAccount: <Name extends LedgerAccountFieldPath<Values>>(
        props: LedgerAccountFieldProps<Values, Name>,
    ) => ReactNode;
    FinancialAccount: {
        <Name extends FinancialAccountFieldPath<Values>>(
            props: FinancialAccountFieldSingleProps<Values, Name>,
        ): ReactNode;
        <Name extends FinancialAccountFieldMultiplePath<Values>>(
            props: FinancialAccountFieldMultipleProps<Name>,
        ): ReactNode;
    };
    Counterparty: {
        <Name extends CounterpartyFieldPath<Values>>(
            props: CounterpartyFieldSingleProps<Values, Name>,
        ): ReactNode;
        <Name extends CounterpartyFieldMultiplePath<Values>>(
            props: CounterpartyFieldMultipleProps<Name>,
        ): ReactNode;
    };
}

/**
 * Returns the typed Formik field kit for a form. Call it once at module level
 * next to the values type: `const fields = createFormikFields<Values>();`,
 * then render `<fields.Date name="date" label="Date" />`. Names are dot
 * paths checked against `Values` and filtered by each field's value type.
 */
export const createFormikFields = <Values>(): FormikFields<Values> => ({
    Text: TextField<Values>,
    TextArea: TextAreaField<Values>,
    Count: CountField<Values>,
    Entity: EntityField<Values>,
    Emails: EmailsField<Values>,
    Phone: PhoneField<Values>,
    Checkbox: CheckboxField<Values>,
    Switch: SwitchField<Values>,
    Date: DateField,
    DateRange: DateRangeField,
    Money: MoneyField,
    Select: SelectField,
    Radio: RadioField,
    LedgerAccount: LedgerAccountField,
    FinancialAccount: FinancialAccountField<Values>,
    Counterparty: CounterpartyField<Values>,
});
